import classNames from "classnames";
import { useCallback, useEffect, useState } from "react";
import ErrorDisplay from "../../components/error/ErrorDisplay";
import { Box, Button } from "@mui/material";
import styles from "./CreateFile.module.css";
import { getHeaders } from "../../utils/common";
import { axiosPrivate } from "../../utils/axiosInstance";
import { DialogWrapper } from "../../components/general/DialogWrapper";
import Loading from "../../Loading";
import { file } from "./FileList";

/**
 * @type {typeOption}
 */
type typeOption = {
  name: string;
  extension: string;
  hoverText: string;
};

const typeOptions: typeOption[] = [
  {
    name: "Flow",
    extension: ".yaml",
    hoverText:
      "A Carbon configuration that describes a continuous stream of data.",
  },
];

/**
 * @typedef {interface} CreateFileParams
 * @prop {string} sessionID
 * @prop {string} subDir
 * @prop {boolean} isOpen
 * @prop {callback} onCreate
 * @prop {callback} closeModal
 */
interface CreateFileParams {
  sessionID: string;
  subDir: string;
  isOpen: boolean;
  onCreate: (fullName?: string, fileType?: string) => void;
  closeModal: () => void;
  files: file[];
}

/**
 * create a file for session it can be yaml or blobl
 * @param {CreateFileParams}
 * @returns {JSX.Element}
 */
export default function CreateFile({
  sessionID,
  subDir,
  onCreate,
  isOpen,
  closeModal,
  files
}: CreateFileParams) {
  const [chosenType, setChosenType] = useState<typeOption | null>(null);
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<Error | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [isUploadInProgress, setIsUploadInProgress] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);

  const myHeaders = getHeaders();
  myHeaders.append("Content-Type", "multipart/form-data");

  useEffect(() => {
    setChosenType(null);
    setIsUploading(false);
    setIsUploadInProgress(false);
    setUploadFiles([]);
    setName("");
    setCreateError(null);
  }, [isOpen]);

  const onFileUploadSelection = useCallback(
    (event: any) => {
      setUploadFiles(event.target.files);
    },
    [setUploadFiles]
  );

  const submitUpload = useCallback(
    (ctx: any) => {
      ctx.preventDefault();

      if (uploadFiles.length === 0) {
        setCreateError(new Error("No files were selected"));
        return;
      }

      const formData = new FormData();

      for (let i = 0; i < uploadFiles.length; i++) {
        formData.append("File", uploadFiles[i]);
      }

      setIsUploadInProgress(true);
      setCreateError(null);

      const uploadPath = `/mqttapi/v1/session/${sessionID}/upload`;

      axiosPrivate
        .postForm(uploadPath, formData, {
          headers: {
            "Content-Type": "multipart/form",
          },
        })
        .then(() => {
          onCreate();
          closeModal();
        })
        .catch((error) => setCreateError(error))
        .finally(() => {
          setIsUploadInProgress(false);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      subDir,
      setCreateError,
      setIsUploadInProgress,
      uploadFiles,
      sessionID,
      onCreate,
      closeModal,
    ]
  );

  const submitCreate = useCallback(
    (ctx: any) => {
      ctx.preventDefault();

      if (!name) {
        setCreateError(new Error("A flow name was not provided"));
        return;
      }

      if (chosenType === null) {
        setCreateError(new Error("A file type was not selected"));
        return;
      }

      if (files.some((f) => f.name === name + (chosenType.extension || ""))) {
        setCreateError(
          new Error(
            `A file with the name "${name}${chosenType.extension}" already exists.`
          )
        );
        return;
      }

      let fullName = name + (chosenType.extension || "");
      if ((subDir || "").length > 0) {
        fullName = `${subDir}/${fullName}`;
      }

      const fileType = "file";
      fetch(
        `/mqttapi/v1/session/${sessionID}/${fileType}/${encodeURIComponent(
          fullName
        )}`,
        {
          method: "POST",
          headers: myHeaders,
        }
      )
        .then(async (response) => {
          if (response.ok) {
            return response.text();
          }
          const text = await response.text();
          throw new Error(`${response.statusText}: ${text}`);
        })
        .then(() => {
          closeModal();
          onCreate(fullName, fileType);
        })
        .catch((error) => setCreateError(error));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, subDir, sessionID, chosenType, onCreate, closeModal]
  );

  const showTypeOptions = useCallback(() => {
    return (
      <div className={styles.modalTypeBtnGroup}>
        <h3>Create new</h3>
        {typeOptions.map((to, idx) => (
          <button
            className={styles.modalTypeBtn}
            key={idx}
            onClick={() => setChosenType(to)}
          >
            <div className={styles.modalTypeTitle}>{to.name}</div>
          </button>
        ))}
        <h3>Import</h3>
        <button
          className={styles.modalTypeBtn}
          onClick={() => setIsUploading(true)}
        >
          <div className={styles.modalTypeTitle}>Upload Files</div>
        </button>
      </div>
    );
  }, [setChosenType]);

  const showUploadView = useCallback(() => {
    return (
      <>
        <h4>
          Upload files
          {(subDir || "").length > 0 ? ` into ${subDir}` : null}
        </h4>
        <input
          type="file"
          name="files"
          onChange={onFileUploadSelection}
          multiple
        />
        <Box sx={{ display: "flex", justifyContent: "right", gap: "20px" }}>
          <Button variant="contained" color="secondary" onClick={submitUpload}>
            Create
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      </>
    );
  }, [closeModal, subDir, onFileUploadSelection, submitUpload]);

  const showConfiguration = useCallback(() => {
    return (
      <form onSubmit={submitCreate}>
        <h4>New {chosenType?.name}</h4>
        <div className="form-group field field-string">
          <label className="control-label">name</label>
          <div className={styles.nameInputContainer}>
            <input
              className={classNames("form-control", styles.nameInput)}
              type="text"
              value={name}
              onChange={(event) => {
                //* not allowing space in file name
                const inputStr = event.currentTarget.value.replace(/\s/g, "");
                setName(inputStr);
              }}
              onSubmit={submitCreate}
            />
            {(chosenType?.extension || "").length > 0 ? (
              <code>{chosenType?.extension}</code>
            ) : null}
          </div>
        </div>
        <Box sx={{ display: "flex", justifyContent: "right", gap: "20px" }}>
          <Button variant="contained" color="secondary" type="submit">
            Create
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      </form>
    );
  }, [closeModal, submitCreate, chosenType, name, setName]);

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth="xs">
      {isUploading ? showUploadView() : null}
      {isUploadInProgress ? <Loading action="Uploading..." /> : null}
      {chosenType === null && !isUploading ? showTypeOptions() : null}
      {chosenType !== null ? showConfiguration() : null}
      {createError !== null ? <ErrorDisplay err={createError} /> : null}
    </DialogWrapper>
  );
}
